// The scorecard grid (HOME-CAD-UX 2026-10-21; KPIS-MODE, founder-approved 2026-10-22; EOS /
// Ninety.io and Bloom Growth scorecards, KPI Beast's tracker). Rows are grouped: the company's own
// KPIs on top ("Company"), then each team (A–Z, its band and score from `scorecards.teams`, its
// own team KPIs one click away) with its seats, then the seats in no team. A seat is a group row
// (holder, seat — a link back to the seat on the chart — band, score), each of its KPIs a row
// with its goal, a sparkline and the last 13 weeks, months or quarters as cells tinted hit
// (green), near (amber) or missed (red); no data is a muted dot, never a red. The company card
// (`children[].metrics`, each row's `trend` carrying the periods) draws every seat in one read.
//
// Entry in place (KPI Beast): an editable cell shows a pencil on hover and the focus ring, and
// is a tab stop. Click it, press Enter or type a digit to enter the number; Enter saves and moves
// down, Tab moves right (Shift reverses), Escape cancels, blur saves; the arrow keys walk every
// cell, `H` (or the context menu, or the clock on hover) opens its history. The value is
// `POST /observations` for the seat and period; the cell waits at half opacity until the card is
// read back. Replacing a past value — or, for a seat's own holder, any value — first asks for the
// reason (the observation's `note`). A cell that takes no number says why on hover
// (`cellBlockReason`: events, formulas, pending review, a future period, a seat not yours, the
// wrong view for the KPI's cadence). Team and company KPI rows are read-only here.

import {
  AvatarTile,
  Badge,
  BandBadge,
  buttonVariants,
  EmptyState,
  LensBandDot,
  ScoreCellInput,
  Sparkline,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, FilterIcon, Rows3Icon, UsersIcon } from "lucide-react";
import { Fragment, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ScorecardChild, ScorecardMetric } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { AgentReportMark } from "../agent-report";
import { metricLabel } from "../cadence/cadence-model";
import { RaiseIssueButton } from "../cadence/issue-dialogs";
import type { CompanyAccess } from "../company";
import { trendLabel } from "../de/scorecard-tab";
import { formatMetricValue, formatScore, formatTarget, seatHolderLabel } from "../format";
import { kpiMetrics } from "../hit-miss";
import type { Holder } from "../home/home-queries";
import { AddKpiDialog } from "../kpi/seat-kpis";
import { type TeamScore, teamKpiKeys } from "../kpi/team-queries";
import { healthCounts, teamBandTone } from "../kpi/team-scorecard";
import { keys } from "../queries";
import { ReportingStatusChip, reportingProps } from "../reporting/reporting-status";
import { Loading } from "../screen-parts";
import { toInputValue, toStoredValue } from "../structure/labels";
import { ErrorNotice } from "../structure/parts";
import { CorrectionPopover, GridScoreCell } from "./scorecard-cell";
import { CellHistoryPopover, type HistoryTarget, historyKey } from "./scorecard-history";
import {
  cellBlockReason,
  columnHeading,
  correctionNeedsReason,
  type DefInfo,
  type Grain,
  gridCaption,
  gridCell,
  gridColumns,
  isOffTrack,
  type TeamLite,
} from "./scorecard-model";

// Kept here for the screens that read cells the grid's way (My scorecard, the DE scorecard).
export {
  cellState,
  editableMetric,
  type GridCell,
  gridCell,
  gridWeeks,
} from "./scorecard-model";

export type GridSeat = {
  child: ScorecardChild;
  holders: Holder[];
};

/** A block of rows: the company's own KPIs, a team (its seats and team KPIs), or "No team". */
export type GridGroup = {
  key: string;
  kind: "company" | "team" | "none";
  team: TeamLite | null;
  seats: GridSeat[];
  /** The company group's KPIs (the company scorecard's own `metrics`). */
  metrics?: ScorecardMetric[];
  /** A team's score and KPI count (`scorecards.teams`); absent when it has no team KPIs. */
  score?: TeamScore;
};

type Subject = { kind: "seat" | "team" | "company"; id: string };
type EditKey = { seat: string; metric: string; col: string };
const keyOf = (k: EditKey) => `${k.seat}|${k.metric}|${k.col}`;
/** A seat KPI row: the keyboard walk and the footer's totals run over these. */
type Row = { seat: GridSeat; metric: ScorecardMetric };
const rowKey = (r: Row) => `${r.seat.child.subject.id}|${r.metric.metric_def_id}`;
const DIGIT = /^[0-9.,-]$/;

type Correction = { k: EditKey; title: string; from: string; to: string; value: number };

export function ScorecardGrid({
  api,
  company,
  grain = "week",
  period,
  current,
  groups,
  currency,
  canEditSeat,
  manager,
  defs,
  offOnly = false,
  focusKpi = null,
  focusSeat = null,
  names,
  canSetup,
}: {
  api: Api;
  company: CompanyAccess;
  grain?: Grain;
  /** The scorecard's period: the last column (the focused week, or this month or quarter). */
  period: string;
  /** The grain's period today (later columns are future periods). */
  current: string;
  groups: GridGroup[];
  currency: string;
  /** A manager, or the seat's own person holder, with the `kpi` add-on writable. */
  canEditSeat: (seatId: string) => boolean;
  /** Managers write without a reason for the current period; holders always give one. */
  manager: boolean;
  /** Metric id → its source, cadence and archive state (what a cell may take). */
  defs: ReadonlyMap<string, DefInfo>;
  /** Only the KPI rows off track this period. */
  offOnly?: boolean;
  /** `?kpi=`: the metric whose current cell gets focus on load (in `focusSeat` when set). */
  focusKpi?: string | null;
  focusSeat?: string | null;
  /** Membership id → name (history's "who"). */
  names: ReadonlyMap<string, string>;
  /** The caller may put metrics on seats (the empty states' "add metrics"). */
  canSetup: boolean;
}) {
  const qc = useQueryClient();
  const [edit, setEditState] = useState<EditKey | null>(null);
  // The edit as of the last call, so a blur that follows a commit does not save twice.
  const editRef = useRef<EditKey | null>(null);
  const setEdit = (k: EditKey | null) => {
    editRef.current = k;
    setEditState(k);
  };
  const [draft, setDraft] = useState("");
  // Values on their way to the API, shown at half opacity until the card is read back.
  const [pending, setPending] = useState<Map<string, string>>(new Map());
  const [correction, setCorrection] = useState<Correction | null>(null);
  const [history, setHistory] = useState<{ key: string; target: HistoryTarget } | null>(null);
  /** MODALS: the seat a KPI is being added to (the KPI editor over the grid). */
  const [addingKpi, setAddingKpi] = useState<GridSeat | null>(null);
  const [openTeams, setOpenTeams] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  /** Cell key → its table cell (the popovers' anchor) and its focusable mark. */
  const tds = useRef(new Map<string, HTMLElement>());
  const marks = useRef(new Map<string, HTMLElement>());
  const refocus = useRef<string | null>(null);
  const focusedKpi = useRef<string | null>(null);
  const cols = gridColumns(grain, period);

  const save = useMutation({
    mutationFn: async (v: EditKey & { value: number; note: string | null }) =>
      api.client.observations.create({
        params: { co: company.id },
        body: {
          metric_def_id: v.metric,
          subject_kind: "seat",
          subject_id: v.seat,
          period_type: grain,
          period_key: v.col,
          value: String(v.value),
          note: v.note,
        },
      }),
    onSettled: async (_row, _err, v) => {
      // The grid reads its card(s) back; the seat's own cards, team roll-ups, the cell's history
      // and the holder's numbers follow.
      const co = company.id;
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["company", co, "scorecard"] }),
        qc.invalidateQueries({ queryKey: [...keys.seatScorecard(co, v.seat, "")].slice(0, 5) }),
        qc.invalidateQueries({ queryKey: historyKey(co) }),
        qc.invalidateQueries({ queryKey: keys.myNumbers(co) }),
        qc.invalidateQueries({ queryKey: ["company", co, "team"] }),
        qc.invalidateQueries({ queryKey: teamKpiKeys.scores(co, "").slice(0, 3) }),
      ]);
      setPending((p) => {
        const next = new Map(p);
        next.delete(keyOf(v));
        return next;
      });
    },
  });

  useEffect(() => {
    if (edit) inputRef.current?.focus();
  }, [edit]);
  // After an edit ends without moving on (Escape, a correction), focus returns to its cell.
  useEffect(() => {
    if (edit || !refocus.current) return;
    marks.current.get(refocus.current)?.focus();
    refocus.current = null;
  });

  // Every seat KPI row, in display order (off-track filter applied).
  const shownMetrics = (ms: readonly ScorecardMetric[]) =>
    kpiMetrics(ms).filter((m) => !offOnly || isOffTrack(m, period, currency));
  const allSeats = groups.flatMap((g) => g.seats);
  const hasRows = (s: GridSeat) => shownMetrics(s.child.metrics ?? []).length > 0;
  const lone = allSeats.length === 1 && !offOnly;
  const scored = (s: GridSeat) => lone || hasRows(s);
  const rows: Row[] = groups.flatMap((g) =>
    g.seats
      .filter(scored)
      .flatMap((seat) =>
        shownMetrics(seat.child.metrics ?? []).map((metric) => ({ seat, metric })),
      ),
  );
  const rowIndex = new Map(rows.map((r, i) => [rowKey(r), i]));
  const companyRows = groups.flatMap((g) =>
    g.kind === "company" ? shownMetrics(g.metrics ?? []) : [],
  );
  const bareSeats = lone || offOnly ? [] : allSeats.filter((s) => !hasRows(s));
  // Focus `?kpi=`'s current cell once its row is drawn (the chart drawer's KPI rows link here).
  useEffect(() => {
    if (!focusKpi || focusedKpi.current === focusKpi) return;
    const r = rows.find(
      (x) =>
        x.metric.metric_def_id === focusKpi &&
        (!focusSeat || x.seat.child.subject.id === focusSeat),
    );
    if (!r) return;
    const el = marks.current.get(
      keyOf({ seat: r.seat.child.subject.id, metric: r.metric.metric_def_id, col: period }),
    );
    if (!el) return;
    focusedKpi.current = focusKpi;
    el.scrollIntoView?.({ block: "center", inline: "nearest" });
    el.focus({ preventScroll: true });
  });
  // Wave 12: the grid opens with its newest column (the scorecard's period: this week, month or
  // quarter) at the right edge, not scrolled to the oldest of the 13; again when the period or
  // the grain changes. A `?kpi=` focus (above) has already scrolled to its cell.
  const scrolledTo = useRef<string | null>(null);
  useEffect(() => {
    const box = tableRef.current?.parentElement;
    const at = `${grain}:${period}`;
    if (!box || scrolledTo.current === at) return;
    scrolledTo.current = at;
    if (!focusKpi) box.scrollLeft = box.scrollWidth;
  });

  const teamGroups = groups.filter((g) => g.kind === "team" && g.score);
  if (!rows.length && !companyRows.length && !bareSeats.length && !teamGroups.length)
    return offOnly ? (
      <EmptyState
        variant="card"
        icon={FilterIcon}
        title="Nothing off track this period"
        description="Every KPI shown met its goal, or has no number yet. Turn off “Off track” to see them all."
      />
    ) : (
      <EmptyState
        variant="card"
        icon={Rows3Icon}
        title="No seat has KPIs yet"
        description={
          canSetup
            ? "Put metrics on a seat under Setup › Seats; the person or DE holding it is scored from then on."
            : "A seat shows here once a manager puts metrics on it."
        }
        actions={
          canSetup ? (
            <Link
              to="/c/$slug/setup/seats"
              params={{ slug: company.slug }}
              search={keepFocus}
              className={buttonVariants({ variant: "secondary", size: "sm" })}
            >
              Open seats
            </Link>
          ) : null
        }
      />
    );

  const cellOf = (m: ScorecardMetric, c: string) => gridCell(m, c, period, currency);
  const hits = cols.map((c) => rows.filter((r) => cellOf(r.metric, c).state === "hit").length);
  const measured = cols.map((c) => rows.filter((r) => cellOf(r.metric, c).state !== "none").length);
  const span = cols.length + 4;
  const anyEditable = allSeats.some((s) => canEditSeat(s.child.subject.id));

  const reasonFor = (subject: Subject, m: ScorecardMetric, c: string) =>
    cellBlockReason({
      subject: subject.kind,
      def: defs.get(m.metric_def_id),
      grain,
      column: c,
      current,
      canEditSubject: subject.kind === "seat" && canEditSeat(subject.id),
      pendingPeriod: m.pending_review?.period_key ?? null,
    });
  const editable = (r: Row, c: string) =>
    reasonFor({ kind: "seat", id: r.seat.child.subject.id }, r.metric, c) === null;
  const start = (r: Row, c: string, typed?: string) => {
    const point = r.metric.trend.find((t) => t.period_key === c);
    setDraft(
      typed ??
        (point?.actual === null || point?.actual === undefined
          ? ""
          : toInputValue(point.actual, r.metric.unit, currency)),
    );
    setEdit({ seat: r.seat.child.subject.id, metric: r.metric.metric_def_id, col: c });
  };
  const submit = (k: EditKey, r: Row, value: number, note: string | null) => {
    setPending((p) => new Map(p).set(keyOf(k), formatMetricValue(value, r.metric.unit, currency)));
    save.mutate({ ...k, value, note });
  };
  const commit = (r: Row, c: string, next: EditKey | null) => {
    const stored = toStoredValue(draft, r.metric.unit, currency);
    const point = r.metric.trend.find((t) => t.period_key === c);
    const had = point?.actual ?? null;
    const unchanged = stored === null || had === stored;
    const k = { seat: r.seat.child.subject.id, metric: r.metric.metric_def_id, col: c };
    if (!unchanged) {
      if (correctionNeedsReason({ hadValue: had !== null, column: c, current, manager })) {
        // A correction waits for its reason: the popover opens on the cell, the walk stops.
        setCorrection({
          k,
          title: `${r.metric.name} · ${columnHeading(grain, c)}`,
          from: formatMetricValue(had, r.metric.unit, currency),
          to: formatMetricValue(stored, r.metric.unit, currency),
          value: stored,
        });
        setEdit(null);
        return;
      }
      submit(k, r, stored, null);
    }
    if (next) {
      const target = rows.find(
        (x) => x.seat.child.subject.id === next.seat && x.metric.metric_def_id === next.metric,
      );
      if (target) start(target, next.col);
      else setEdit(null);
    } else {
      refocus.current = keyOf(k);
      setEdit(null);
    }
  };
  /** The next editable cell: down (`dRow`) or sideways (`dCol`), skipping read-only cells. */
  const step = (r: Row, c: string, dRow: number, dCol: number): EditKey | null => {
    let ri = rowIndex.get(rowKey(r)) ?? -1;
    let ci = cols.indexOf(c);
    for (let n = 0; n < rows.length * cols.length; n++) {
      ri += dRow;
      ci += dCol;
      if (dCol && (ci < 0 || ci >= cols.length)) {
        ci = ci < 0 ? cols.length - 1 : 0;
        ri += ci === 0 ? 1 : -1;
      }
      if (ri < 0 || ri >= rows.length) return null;
      const row = rows[ri];
      const col = cols[ci];
      if (row && col && editable(row, col))
        return { seat: row.seat.child.subject.id, metric: row.metric.metric_def_id, col };
    }
    return null;
  };
  /** Arrow keys walk every seat cell (read-only ones too: their reason shows on focus). */
  const walk = (ri: number, ci: number, e: KeyboardEvent<HTMLElement>) => {
    const d =
      e.key === "ArrowDown"
        ? [1, 0]
        : e.key === "ArrowUp"
          ? [-1, 0]
          : e.key === "ArrowRight"
            ? [0, 1]
            : e.key === "ArrowLeft"
              ? [0, -1]
              : null;
    if (!d || ri < 0) return false;
    const el = tableRef.current?.querySelector<HTMLElement>(
      `[data-cell][data-r="${ri + (d[0] ?? 0)}"][data-c="${ci + (d[1] ?? 0)}"]`,
    );
    e.preventDefault();
    el?.focus();
    return true;
  };

  const historyTarget = (subject: Subject, m: ScorecardMetric, c: string): HistoryTarget => ({
    metric: m.metric_def_id,
    metricName: m.name,
    unit: m.unit,
    subject,
    grain,
    period: c,
    title: `${m.name} · ${columnHeading(grain, c)}`,
  });

  /** One KPI row: name and marks, goal, trend, the period cells, hits. */
  const kpiRow = (subject: Subject, m: ScorecardMetric, seat: GridSeat | null): ReactNode => {
    const row: Row | null = seat ? { seat, metric: m } : null;
    const ri = row ? (rowIndex.get(rowKey(row)) ?? -1) : -1;
    const rowHits = cols.filter((c) => cellOf(m, c).state === "hit").length;
    const rowMeasured = cols.filter((c) => cellOf(m, c).state !== "none").length;
    return (
      <TableRow
        key={`${subject.id}:${m.metric_def_id}`}
        data-metric={m.key}
        data-metric-id={m.metric_def_id}
        data-subject={subject.kind}
        className="group/row"
      >
        <TableHead scope="row" className="sticky left-0 z-10 font-normal [&[scope=row]]:bg-card">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium">{m.name}</span>
            {m.visibility === "agency" ? <Badge variant="accent">Agency</Badge> : null}
            {m.reporting || m.pending_review ? null : <AgentReportMark metric={m} />}
            {/* MEETINGS: a missed KPI of the period is raised as an issue. */}
            {m.attainment !== null && m.attainment < 1 ? (
              <RaiseIssueButton
                api={api}
                co={company.id}
                label={`Raise ${m.name} as an issue`}
                className="ml-auto opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                defaults={{
                  title: `${m.name} below target`,
                  ...(subject.kind === "seat" ? { owner_seat_id: subject.id } : {}),
                  priority: 1,
                  source: {
                    kind: "metric",
                    id: m.metric_def_id,
                    label: metricLabel(
                      m.name,
                      period,
                      formatMetricValue(m.actual, m.unit, currency),
                      formatMetricValue(m.target, m.unit, currency),
                    ),
                  },
                }}
              />
            ) : null}
          </span>
          {/* AGENT-REPORTING: the duty's status (and the agent's mark) on a second line. */}
          {m.reporting || m.pending_review ? (
            <span className="mt-0.5 flex min-w-0 items-center gap-1">
              <AgentReportMark metric={m} />
              <ReportingStatusChip compact {...reportingProps(m)} />
            </span>
          ) : null}
        </TableHead>
        <TableCell className="whitespace-nowrap text-right text-muted-foreground tabular-nums">
          {formatTarget(m, currency)}
        </TableCell>
        <TableCell className="max-md:hidden">
          <Sparkline
            label={trendLabel(m, currency, period)}
            values={m.trend.map((p) => p.actual)}
            reference={m.trend.map((p) => p.target)}
          />
        </TableCell>
        {cols.map((c, ci) => {
          const k = { seat: subject.id, metric: m.metric_def_id, col: c };
          const key = keyOf(k);
          const cell = cellOf(m, c);
          const reason = reasonFor(subject, m, c);
          const editing = row && edit && keyOf(edit) === key;
          const name = `${m.name}, ${columnHeading(grain, c)}`;
          const openHistory =
            cell.value !== null
              ? () => setHistory({ key, target: historyTarget(subject, m, c) })
              : null;
          return (
            <TableCell
              key={c}
              ref={(el: HTMLElement | null) => {
                if (el) tds.current.set(key, el);
                else tds.current.delete(key);
              }}
              data-week={c}
              data-period={c}
              data-current={c === current || undefined}
              data-editable={reason === null || undefined}
              className="group/td relative w-16 p-0 align-middle"
            >
              {editing && row ? (
                <ScoreCellInput
                  ref={inputRef}
                  aria-label={name}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commit(row, c, step(row, c, e.shiftKey ? -1 : 1, 0));
                    } else if (e.key === "Tab") {
                      e.preventDefault();
                      commit(row, c, step(row, c, 0, e.shiftKey ? -1 : 1));
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      refocus.current = key;
                      setEdit(null);
                    }
                  }}
                  onBlur={() => {
                    if (editRef.current && keyOf(editRef.current) === key) commit(row, c, null);
                  }}
                />
              ) : (
                <GridScoreCell
                  cell={cell}
                  inFlight={pending.get(key)}
                  reason={reason}
                  name={name}
                  coords={{ r: ri, c: ci }}
                  cellRef={(el: HTMLElement | null) => {
                    if (el) marks.current.set(key, el);
                    else marks.current.delete(key);
                  }}
                  onEdit={() => (row ? start(row, c) : undefined)}
                  onHistory={openHistory}
                  onKeyDown={(e) => {
                    if (e.altKey || e.metaKey || e.ctrlKey) return;
                    if (walk(ri, ci, e)) return;
                    if (
                      (e.key === "h" || e.key === "H" || e.key === "ContextMenu") &&
                      openHistory
                    ) {
                      e.preventDefault();
                      openHistory();
                    } else if (row && reason === null && (DIGIT.test(e.key) || e.key === "F2")) {
                      e.preventDefault();
                      start(row, c, e.key === "F2" ? undefined : e.key);
                    }
                  }}
                />
              )}
            </TableCell>
          );
        })}
        <TableCell className="text-right text-muted-foreground tabular-nums">
          {rowMeasured ? `${rowHits}/${rowMeasured}` : "—"}
        </TableCell>
      </TableRow>
    );
  };

  const seatBlock = (seat: GridSeat): ReactNode[] => {
    const metrics = shownMetrics(seat.child.metrics ?? []);
    const lead = seat.holders[0];
    const de = seat.holders.find((h) => h.kind === "digital_employee");
    const name = lead?.name ?? seat.child.subject.name;
    const id = seat.child.subject.id;
    return [
      <TableRow key={`${id}:seat`} data-seat={id} className="bg-sunken hover:bg-sunken">
        <TableCell colSpan={span} className="sticky left-0 z-10 bg-sunken py-1.5">
          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            {/* The row spans every column, so its name sticks on its own when the grid scrolls sideways. */}
            <span className="sticky left-0 z-[1] -ml-4 flex min-w-0 items-center gap-2 bg-sunken pr-2 pl-4">
              <AvatarTile
                name={name}
                kind={lead?.kind === "digital_employee" ? "agent" : "person"}
                size="sm"
              />
              {/* KPIS-MODE: the seat opens on the chart (its drawer, `?seat=`). */}
              <Link
                to="/c/$slug/kpis/chart"
                params={{ slug: company.slug }}
                search={(prev) => ({ ...keepFocus(prev as Record<string, unknown>), seat: id })}
                className="truncate font-medium text-foreground hover:underline"
                data-slot="seat-chart-link"
                title={`Open ${seat.child.subject.name} on the chart`}
              >
                {seat.child.subject.name}
              </Link>
              {lead && name !== seat.child.subject.name ? (
                <span className="truncate text-muted-foreground">
                  {"· "}
                  {de ? (
                    <Link
                      to="/c/$slug/kpis/de/$id"
                      params={{ slug: company.slug, id: de.membership_id }}
                      search={keepFocus}
                      className="text-foreground hover:underline"
                    >
                      {name}
                    </Link>
                  ) : (
                    <span className="text-foreground">{name}</span>
                  )}
                </span>
              ) : null}
            </span>
            <span className="ml-auto flex items-center gap-2">
              <BandBadge band={seat.child.band} />
              <span className="w-14 text-right text-sm text-foreground tabular-nums">
                {seat.child.kpi_score === null ? "—" : formatScore(seat.child.kpi_score)}
              </span>
            </span>
          </span>
        </TableCell>
      </TableRow>,
      ...(!metrics.length
        ? [
            <TableRow key={`${id}:none`}>
              <TableCell colSpan={span} className="text-muted-foreground">
                No KPI on this seat yet
                {canSetup ? (
                  <>
                    {" · "}
                    {/* MODALS: the KPI editor over the scorecard, not Setup › Seats. */}
                    <button
                      type="button"
                      className="text-primary-text hover:underline"
                      onClick={() => setAddingKpi(seat)}
                    >
                      add a KPI
                    </button>
                  </>
                ) : null}
              </TableCell>
            </TableRow>,
          ]
        : metrics.map((m) => kpiRow({ kind: "seat", id }, m, seat))),
    ];
  };

  const showHeaders = groups.some((g) => g.kind !== "none");
  const groupHeader = (g: GridGroup): ReactNode => {
    if (!showHeaders) return null;
    const open = g.team ? openTeams.has(g.team.id) : false;
    const label = g.kind === "company" ? "Company" : g.kind === "none" ? "No team" : g.team?.name;
    return (
      <TableRow
        key={`${g.key}:group`}
        data-team={g.kind === "team" ? g.team?.id : g.kind}
        data-slot="scorecard-group"
        className="hover:bg-transparent"
      >
        <TableCell colSpan={span} className="sticky left-0 z-10 bg-card pt-4 pb-1.5">
          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="sticky left-0 z-[1] -ml-4 flex min-w-0 items-center gap-2 bg-card pr-2 pl-4 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              <UsersIcon aria-hidden="true" className="size-3.5" />
              <span className="truncate">{label}</span>
            </span>
            {g.score ? (
              <>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <LensBandDot band={teamBandTone(g.score)} />
                  <span className="text-foreground tabular-nums">
                    {formatScore(g.score.kpi_score)}
                  </span>
                  <span className="max-sm:hidden">· {healthCounts(g.score)}</span>
                </span>
                {g.team && g.score.metrics > 0 ? (
                  <button
                    type="button"
                    aria-expanded={open}
                    data-slot="team-kpis-toggle"
                    onClick={() =>
                      setOpenTeams((s) => {
                        const next = new Set(s);
                        const id = g.team?.id as string;
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        return next;
                      })
                    }
                    className="ml-auto flex items-center gap-1 rounded-sm px-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    <ChevronRightIcon
                      aria-hidden="true"
                      className={`size-3.5 transition-transform ${open ? "rotate-90" : ""}`}
                    />
                    Team KPIs ({g.score.metrics})
                  </button>
                ) : null}
              </>
            ) : null}
          </span>
        </TableCell>
      </TableRow>
    );
  };

  const target = history ? tds.current.get(history.key) : null;
  const fixing = correction ? tds.current.get(keyOf(correction.k)) : null;

  return (
    <>
      {save.isError ? <ErrorNotice what="The number" error={save.error} /> : null}
      <Table
        ref={tableRef}
        data-slot="scorecard-grid"
        data-grain={grain}
        className="min-w-[72rem] table-fixed"
      >
        <TableCaption>
          KPI hit or miss for the last {cols.length}{" "}
          {grain === "week" ? "weeks" : grain === "month" ? "months" : "quarters"}, oldest first
          {anyEditable ? "; click or Tab into a cell to type its number" : ""}.
          <span className="sr-only"> {gridCaption(grain, anyEditable, cols.length)}</span>
        </TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead
              scope="col"
              className="sticky left-0 z-10 w-60 bg-sunken [thead_&:first-child]:z-20"
            >
              Measurable
            </TableHead>
            <TableHead scope="col" className="w-16 text-right">
              Goal
            </TableHead>
            <TableHead scope="col" className="w-24 max-md:hidden">
              Trend
            </TableHead>
            {cols.map((c) => (
              <TableHead
                key={c}
                scope="col"
                data-week={c}
                data-period={c}
                data-current={c === current || undefined}
                className="w-16 px-2 text-center tabular-nums data-current:text-primary-text"
              >
                {columnHeading(grain, c)}
                <span className="sr-only"> {c.slice(0, 4)}</span>
              </TableHead>
            ))}
            <TableHead scope="col" className="w-14 text-right">
              Hit
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((g) => {
            if (g.kind === "company") {
              const ms = shownMetrics(g.metrics ?? []);
              if (!ms.length) return null;
              return [
                groupHeader(g),
                ...ms.map((m) => kpiRow({ kind: "company", id: company.id }, m, null)),
              ];
            }
            const seats = g.seats.filter(scored);
            const open = g.team && openTeams.has(g.team.id);
            if (!seats.length && !(g.score && g.score.metrics > 0)) return null;
            return [
              groupHeader(g),
              open && g.team ? (
                <TeamKpiRows
                  key={`${g.key}:team-kpis`}
                  api={api}
                  co={company.id}
                  team={g.team}
                  grain={grain}
                  period={period}
                  span={span}
                  render={(ms) =>
                    ms
                      .filter((m) => !offOnly || isOffTrack(m, period, currency))
                      .map((m) => kpiRow({ kind: "team", id: g.team?.id as string }, m, null))
                  }
                />
              ) : null,
              ...seats.flatMap(seatBlock),
            ];
          })}
          {bareSeats.length ? (
            // Seats with no KPI yet take one line, not a block each (EOS lists measurables,
            // not people without any); a seat in focus still gets its own row above.
            <TableRow data-slot="scorecard-bare-seats">
              <TableCell colSpan={span} className="text-muted-foreground">
                No KPI yet:{" "}
                {canSetup
                  ? // MODALS: each seat opens the KPI editor for it, over the scorecard.
                    bareSeats.map((s, i) => (
                      <Fragment key={s.child.subject.id}>
                        {i ? ", " : null}
                        <button
                          type="button"
                          className="text-primary-text hover:underline"
                          aria-label={`Add a KPI to ${s.child.subject.name}`}
                          onClick={() => setAddingKpi(s)}
                        >
                          {seatHolderLabel(s.child.subject.name, s.holders[0]?.name)}
                        </button>
                      </Fragment>
                    ))
                  : bareSeats
                      .map((s) => seatHolderLabel(s.child.subject.name, s.holders[0]?.name))
                      .join(", ")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableHead scope="row" className="sticky left-0 z-10 [&[scope=row]]:bg-sunken">
              KPIs hit
            </TableHead>
            <TableCell />
            <TableCell className="max-md:hidden" />
            {cols.map((c, i) => (
              <TableCell key={c} className="px-2 text-center tabular-nums" data-week={c}>
                {measured[i] ? `${hits[i]}/${measured[i]}` : "—"}
              </TableCell>
            ))}
            <TableCell className="text-right tabular-nums">
              {rows.length
                ? `${hits.reduce((a, b) => a + b, 0)}/${measured.reduce((a, b) => a + b, 0)}`
                : "—"}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
      {history ? (
        <CellHistoryPopover
          api={api}
          co={company.id}
          currency={currency}
          target={history.target}
          anchor={target ?? null}
          names={names}
          onClose={() => {
            refocus.current = history.key;
            setHistory(null);
          }}
        />
      ) : null}
      {correction ? (
        <CorrectionPopover
          key={keyOf(correction.k)}
          anchor={fixing ?? null}
          title={correction.title}
          from={correction.from}
          to={correction.to}
          saving={save.isPending}
          onCancel={() => {
            refocus.current = keyOf(correction.k);
            setCorrection(null);
          }}
          onSave={(reason) => {
            const r = rows.find(
              (x) =>
                x.seat.child.subject.id === correction.k.seat &&
                x.metric.metric_def_id === correction.k.metric,
            );
            if (r) submit(correction.k, r, correction.value, reason);
            refocus.current = keyOf(correction.k);
            setCorrection(null);
          }}
        />
      ) : null}
      {addingKpi ? (
        <AddKpiDialog
          api={api}
          company={company}
          seatId={addingKpi.child.subject.id}
          seatTitle={addingKpi.child.subject.name}
          holder={{
            text: addingKpi.holders.map((h) => h.name).join(" · ") || "Open seat",
            agent: addingKpi.holders.some((h) => h.kind === "digital_employee"),
          }}
          onClose={() => setAddingKpi(null)}
        />
      ) : null}
    </>
  );
}

/** A team's own KPI rows, read when its group is opened (`teams.scorecard` for the period). */
function TeamKpiRows({
  api,
  co,
  team,
  grain,
  period,
  span,
  render,
}: {
  api: Api;
  co: string;
  team: TeamLite;
  grain: Grain;
  period: string;
  span: number;
  render: (metrics: ScorecardMetric[]) => ReactNode[];
}) {
  const q = useQuery({
    queryKey: [...teamKpiKeys.scorecard(co, team.id, period), grain] as const,
    queryFn: () =>
      api.client.teams.scorecard({
        params: { co, id: team.id },
        query: { period_type: grain, period },
      }),
  });
  if (q.isPending || q.isError)
    return (
      <TableRow data-slot="team-kpi-rows" data-team-kpis={team.id}>
        <TableCell colSpan={span}>
          {q.isError ? (
            <ErrorNotice what={`${team.name}'s KPIs`} error={q.error} />
          ) : (
            <Loading label={`Loading ${team.name}'s KPIs`} rows={1} />
          )}
        </TableCell>
      </TableRow>
    );
  const rows = render(kpiMetrics(q.data.metrics));
  if (rows.length) return rows;
  return (
    <TableRow data-slot="team-kpi-rows" data-team-kpis={team.id}>
      <TableCell colSpan={span} className="text-muted-foreground">
        No team KPI to show for this period.
      </TableCell>
    </TableRow>
  );
}
